import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Users, Bell } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export function HorariosTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [schedules, setSchedules] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [sRes, mRes] = await Promise.all([
        authAxiosRef.current.get("/timeclock/schedules"),
        authAxiosRef.current.get("/deliverers/team/members"),
      ]);
      setSchedules(sRes.data);
      setMembers(mRes.data.filter(m => m.invite_accepted));
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const scheduleMap = {};
  schedules.forEach(s => { scheduleMap[s.member_id] = s; });

  const handleChange = (memberId, field, value) =>
    setPending(prev => ({ ...prev, [memberId]: { ...prev[memberId], [field]: value } }));

  const toggleDay = (memberId, dayIndex) => {
    const sch = scheduleMap[memberId] || {};
    const edits = pending[memberId] || {};
    const current = edits.days_of_week !== undefined ? edits.days_of_week : (sch.days_of_week ?? [0, 1, 2, 3, 4]);
    const next = current.includes(dayIndex) ? current.filter(d => d !== dayIndex) : [...current, dayIndex].sort();
    handleChange(memberId, "days_of_week", next);
  };

  const saveSchedule = async (member) => {
    const sch = scheduleMap[member.id] || {};
    const edits = pending[member.id] || {};
    const get = (field, def) => edits[field] !== undefined ? edits[field] : (sch[field] ?? def);
    const payload = {
      member_name: member.name,
      expected_hours: parseFloat(get("expected_hours", 8)) || 8,
      start_time: get("start_time", null) || null,
      end_time: get("end_time", null) || null,
      break_minutes: parseInt(get("break_minutes", 0)) || 0,
      late_tolerance_minutes: parseInt(get("late_tolerance_minutes", 10)) || 10,
      days_of_week: get("days_of_week", [0, 1, 2, 3, 4]),
      active: get("active", true),
    };
    try {
      await authAxios.put(`/timeclock/schedules/${member.id}`, payload);
      toast.success("Horário guardado");
      setPending(prev => { const n = { ...prev }; delete n[member.id]; return n; });
      fetchData();
    } catch { toast.error("Erro ao guardar"); }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4" data-testid="horarios-tab">
      <div>
        <p className="text-sm text-muted-foreground">
          Defina turnos, dias de trabalho e tolerâncias por funcionário.
          O sistema enviará avisos por WhatsApp 5 min antes da entrada/saída.
        </p>
        <div className="flex items-center gap-2 mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <Bell className="w-3.5 h-3.5 shrink-0" />
          <span>Lembrete enviado se o funcionário tiver número de telemóvel no perfil de equipa.</span>
        </div>
      </div>

      <div className="space-y-3">
        {members.map(m => {
          const sch = scheduleMap[m.id] || {};
          const edits = pending[m.id] || {};
          const get = (field, def) => edits[field] !== undefined ? edits[field] : (sch[field] ?? def);
          const isDirty = !!pending[m.id];
          const activeDays = get("days_of_week", [0, 1, 2, 3, 4]);

          return (
            <Card key={m.id} className="border-border/50" data-testid={`schedule-${m.id?.slice(-6)}`}>
              <CardContent className="p-4 space-y-4">
                {/* Header row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-emerald-700">{m.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{m.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{m.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Ativo</span>
                    <Switch
                      checked={get("active", true)}
                      onCheckedChange={v => handleChange(m.id, "active", v)}
                    />
                  </div>
                </div>

                {/* Time inputs */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground block mb-1">Horas/dia</Label>
                    <Input type="number" step="0.5" min="1" max="24"
                      value={get("expected_hours", 8)}
                      onChange={e => handleChange(m.id, "expected_hours", e.target.value)}
                      className="h-8 text-center font-bold"
                      data-testid={`hours-input-${m.id?.slice(-6)}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground block mb-1">Entrada</Label>
                    <Input type="time" value={get("start_time", "")}
                      onChange={e => handleChange(m.id, "start_time", e.target.value)}
                      className="h-8 text-center font-mono"
                      data-testid={`start-time-${m.id?.slice(-6)}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground block mb-1">Saída</Label>
                    <Input type="time" value={get("end_time", "")}
                      onChange={e => handleChange(m.id, "end_time", e.target.value)}
                      className="h-8 text-center font-mono"
                      data-testid={`end-time-${m.id?.slice(-6)}`}
                    />
                  </div>
                </div>

                {/* Break + Tolerance */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground block mb-1">Pausa (min)</Label>
                    <Input type="number" min="0" max="120" step="5"
                      value={get("break_minutes", 0)}
                      onChange={e => handleChange(m.id, "break_minutes", e.target.value)}
                      className="h-8 text-center"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground block mb-1">Tolerância atraso (min)</Label>
                    <Input type="number" min="0" max="60" step="5"
                      value={get("late_tolerance_minutes", 10)}
                      onChange={e => handleChange(m.id, "late_tolerance_minutes", e.target.value)}
                      className="h-8 text-center"
                      placeholder="10"
                    />
                  </div>
                </div>

                {/* Days of week */}
                <div>
                  <Label className="text-xs text-muted-foreground block mb-2">Dias de trabalho</Label>
                  <div className="flex gap-1 flex-wrap">
                    {DAYS.map((day, i) => {
                      const active = activeDays.includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => toggleDay(m.id, i)}
                          className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${
                            active
                              ? "bg-emerald-600 text-white border-emerald-600"
                              : "bg-background text-muted-foreground border-border hover:border-emerald-400"
                          }`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {isDirty && (
                  <Button size="sm" className="w-full" onClick={() => saveSchedule(m)}
                    data-testid={`save-schedule-${m.id?.slice(-6)}`}>
                    Guardar
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}

        {members.length === 0 && (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <Users className="w-10 h-10 mx-auto mb-3" />
            <p>Adicione membros na aba "Equipo" primeiro</p>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
